import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Alert, Divider, Stack, Snackbar } from '@mui/material';
import LocationSearch, { LocationResult } from './LocationSearch';

interface LocationSearchExampleProps {
  onSave?: (location: LocationResult) => void;
  onClose?: () => void;
  title?: string;
}

const LocationSearchExample: React.FC<LocationSearchExampleProps> = ({
  onSave,
  onClose,
  title = "Location Search Example"
}) => {
  const [location, setLocation] = useState<LocationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleLocationChange = (newLocation: LocationResult | null) => {
    setLocation(newLocation);
    setError(null);
  };

  const handleError = (err: Error) => {
    setError(err.message);
  };

  const handleClear = () => {
    setLocation(null);
    setError(null);
  };

  const handleSave = () => {
    if (location && onSave) {
      onSave(location);
      setSnackbarOpen(true);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <LocationSearch 
          label="Search for a location"
          placeholder="Enter a city name"
          onChange={handleLocationChange}
          onError={handleError}
          value={location}
          fullWidth
        />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {location && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Selected Location
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Typography variant="body1" gutterBottom>
              <strong>Name:</strong> {location.mainText}
            </Typography>
            
            <Typography variant="body1" gutterBottom>
              <strong>Address:</strong> {location.description}
            </Typography>
            
            <Typography variant="body1" gutterBottom>
              <strong>Coordinates:</strong> {location.coordinates.lat.toFixed(6)}, {location.coordinates.lng.toFixed(6)}
            </Typography>
            
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Place ID:</strong> {location.placeId}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Stack direction="row" spacing={2} justifyContent="space-between">
        <Button 
          variant="outlined" 
          color="secondary" 
          onClick={handleClear}
          disabled={!location}
        >
          Clear Selection
        </Button>
        
        <Stack direction="row" spacing={2}>
          {onClose && (
            <Button 
              variant="outlined" 
              color="inherit" 
              onClick={onClose}
            >
              Cancel
            </Button>
          )}
          
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSave}
            disabled={!location}
          >
            Save Location
          </Button>
        </Stack>
      </Stack>
      
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        message="Location saved successfully"
      />
    </Box>
  );
};

export default LocationSearchExample; 